import React, {useState, useEffect} from 'react';
import {
    BarChart3,
    BookOpen,
    Calendar,
    CheckCircle,
    Clock,
    FileText,
    MessageSquare,
    Shield,
    Users,
    Heart,
    Star,
    Award,
    TrendingUp,
    ChevronDown,
    AlertCircle,
    Loader
} from 'lucide-react';
import Layout from '../Layout';
import ParentAPI from '../services/ParentAPI';
import { showErrorNotification, showSuccessNotification } from '../utils/errorHandler';

const HomePage = () => {
    const [selectedChild, setSelectedChild] = useState(null);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Data states
    const [parentInfo, setParentInfo] = useState(null);
    const [children, setChildren] = useState([]);
    const [dashboardData, setDashboardData] = useState(null);
    const [attendanceStats, setAttendanceStats] = useState(null);
    const [learningActivities, setLearningActivities] = useState([]);
    const [competencyData, setCompetencyData] = useState(null);
    const [assessmentResults, setAssessmentResults] = useState([]);
    const [holisticProgress, setHolisticProgress] = useState(null);
    const [partnershipData, setPartnershipData] = useState(null);

    // Update clock every second
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    // Load initial data
    useEffect(() => {
        loadInitialData();
    }, []);

    // Load child-specific data when selected child changes
    useEffect(() => {
        if (selectedChild) {
            loadChildData(selectedChild);
        }
    }, [selectedChild]);

    const loadInitialData = async () => {
        try {
            setLoading(true);
            setError(null);

            // Get parent info and children
            const [userInfo, childrenList] = await Promise.all([
                ParentAPI.getCurrentUser(),
                ParentAPI.getChildren()
            ]);

            setParentInfo(userInfo);
            setChildren(childrenList);

            // Set first child as selected if available
            if (childrenList.length > 0) {
                setSelectedChild(childrenList[0].student_id);
            }

            // Load partnership data (not child-specific)
            const partnership = await ParentAPI.getPartnershipData();
            setPartnershipData(partnership);

        } catch (err) {
            console.error('Error loading initial data:', err);
            const errorMessage = err.friendlyMessage || 'Failed to load dashboard data. Please try again.';
            setError(errorMessage);
            showErrorNotification(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const loadChildData = async (childId) => {
        try {
            setLoading(true);
            
            const currentDate = new Date();
            const month = currentDate.getMonth();
            const year = currentDate.getFullYear();

            // Load all child-specific data
            const [
                dashboard,
                attendance,
                activities,
                competency,
                assessments,
                holistic
            ] = await Promise.all([
                ParentAPI.getDashboardData(childId),
                ParentAPI.getAttendanceStats(childId, month, year),
                ParentAPI.getRecentLearningActivities(childId),
                ParentAPI.getCompetencyData(childId),
                ParentAPI.getAssessmentResults(childId),
                ParentAPI.getHolisticProgress(childId)
            ]);

            setDashboardData(dashboard);
            setAttendanceStats(attendance);
            setLearningActivities(activities);
            setCompetencyData(competency);
            setAssessmentResults(assessments);
            setHolisticProgress(holistic);

        } catch (err) {
            console.error('Error loading child data:', err);
            const errorMessage = err.friendlyMessage || 'Failed to load child data. Please try again.';
            setError(errorMessage);
            showErrorNotification(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const formatTime = (date) => {
        return date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
        });
    };

    const formatDate = (date) => {
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const handleChildSwitch = (childId) => {
        setSelectedChild(childId);
        const child = children.find(c => c.student_id === childId);
        if (child) {
            // Show a brief notification about switching
            console.log(`Switched to ${child.name}'s dashboard`);
        }
    };

    const viewAttendanceDetails = (date) => {
        setSelectedDate(date);
    };

    const getAttendanceData = (day) => {
        if (!attendanceStats || !attendanceStats.dailyData) {
            return {
                status: 'Present',
                checkin: '8:15 AM',
                classes: '6/6',
                note: 'Regular attendance.'
            };
        }
        
        const dayData = attendanceStats.dailyData[day];
        if (dayData) {
            return {
                status: dayData.status,
                checkin: dayData.status === 'Present' ? '8:15 AM' : 'N/A',
                classes: dayData.status === 'Present' ? '6/6' : '0/6',
                note: dayData.note || 'Regular attendance.'
            };
        }
        
        return {
            status: 'Present',
            checkin: '8:15 AM',
            classes: '6/6',
            note: 'Regular attendance.'
        };
    };

    // Get current child info
    const getCurrentChild = () => {
        if (!selectedChild || !children.length) return null;
        return children.find(child => child.student_id === selectedChild);
    };

    // Loading component
    if (loading && !dashboardData) {
        return (
            <Layout pageTitle="Parent Portal">
                <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 font-['Poppins',sans-serif] flex items-center justify-center">
                    <div className="text-center">
                        <Loader className="w-12 h-12 animate-spin mx-auto mb-4 text-green-600" />
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Loading Dashboard</h2>
                        <p className="text-gray-600">Please wait while we fetch your child's information...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    // Error component
    if (error) {
        return (
            <Layout pageTitle="Parent Portal">
                <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 font-['Poppins',sans-serif] flex items-center justify-center">
                    <div className="text-center">
                        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Error Loading Dashboard</h2>
                        <p className="text-gray-600 mb-4">{error}</p>
                        <button 
                            onClick={loadInitialData}
                            className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg transition-colors"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            </Layout>
        );
    }

    const currentChild = getCurrentChild();

    return (
        <Layout pageTitle="Parent Portal">
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');
                
                .card-parent {
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    border: 2px solid transparent;
                    background: linear-gradient(white, white) padding-box,
                                linear-gradient(135deg, #059669, #3b82f6) border-box;
                }
                
                .card-parent:hover {
                    transform: translateY(-8px) scale(1.02);
                    box-shadow: 0 25px 50px rgba(5, 150, 105, 0.2);
                }
                
                .neon-glow {
                    box-shadow: 0 0 20px rgba(5, 150, 105, 0.5);
                }
                
                .pulse-animation {
                    animation: pulse 2s infinite;
                }
                
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.7; }
                }
                
                .calendar-day {
                    transition: all 0.2s ease;
                    border-radius: 12px;
                }
                
                .calendar-day:hover {
                    transform: scale(1.1);
                }
                
                .present-day { 
                    background: linear-gradient(135deg, #059669, #047857); 
                    color: white; 
                    box-shadow: 0 4px 15px rgba(5, 150, 105, 0.4);
                }
                
                .absent-day { 
                    background: linear-gradient(135deg, #dc2626, #b91c1c); 
                    color: white; 
                    box-shadow: 0 4px 15px rgba(220, 38, 38, 0.4);
                }
                
                .holiday-day { 
                    background: linear-gradient(135deg, #7c3aed, #6d28d9); 
                    color: white; 
                    box-shadow: 0 4px 15px rgba(124, 58, 237, 0.4);
                }
                
                .learning-card {
                    background: linear-gradient(135deg, rgba(5, 150, 105, 0.1), rgba(59, 130, 246, 0.1));
                    border: 2px solid transparent;
                    background-clip: padding-box;
                    transition: all 0.3s ease;
                }
                
                .learning-card:hover {
                    background: linear-gradient(135deg, rgba(5, 150, 105, 0.2), rgba(59, 130, 246, 0.2));
                    transform: translateY(-4px);
                }
                
                .competency-ring {
                    background: conic-gradient(
                        #059669 0deg 108deg,
                        #3b82f6 108deg 180deg,
                        #10b981 180deg 252deg,
                        #7c3aed 252deg 324deg,
                        #f59e0b 324deg 360deg
                    );
                    border-radius: 50%;
                    position: relative;
                }
                
                .competency-center {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    background: white;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-direction: column;
                }
                
                .progress-bar {
                    background: linear-gradient(90deg, #059669, #3b82f6);
                    border-radius: 10px;
                    height: 8px;
                    transition: width 0.5s ease;
                }
                
                .floating-icon {
                    animation: float 3s ease-in-out infinite;
                }
                
                @keyframes float {
                    0%, 100% { transform: translateY(0px); }
                    50% { transform: translateY(-10px); }
                }
                
                .alert-badge {
                    background: linear-gradient(135deg, #f59e0b, #d97706);
                    color: white;
                    padding: 4px 12px;
                    border-radius: 20px;
                    font-size: 12px;
                    font-weight: 600;
                    animation: pulse 2s infinite;
                }
                
                .digital-clock {
                    background: rgba(255, 255, 255, 0.2);
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255, 255, 255, 0.3);
                    color: white;
                    padding: 16px 24px;
                    border-radius: 16px;
                    font-family: 'Courier New', monospace;
                    text-align: center;
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
                }
            `}</style>

            <div
                className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 font-['Poppins',sans-serif]">
                {/* Header */}
                <header className="bg-gradient-to-r from-green-600 to-blue-600 shadow-2xl">
                    <div className="max-w-7xl mx-auto px-6 py-6">
                        <div className="flex items-center justify-between flex-wrap gap-4">
                            <div className="flex items-center space-x-4">
                                <div className="floating-icon">
                                    <CheckCircle className="w-12 h-12 text-white"/>
                                </div>
                                <div>
                                    <h1 className="text-3xl font-bold text-white">
                                        Welcome, {parentInfo?.name || 'Parent'}!
                                    </h1>
                                    <p className="text-green-100">
                                        {currentChild ? `${currentChild.name}'s Learning Journey Dashboard` : "Select a child to view dashboard"}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-4">
                                <div className="relative">
                                    <select
                                        value={selectedChild || ''}
                                        onChange={(e) => handleChildSwitch(e.target.value)}
                                        className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium border border-white/30 appearance-none cursor-pointer pr-10"
                                        disabled={!children.length}
                                    >
                                        {children.length === 0 ? (
                                            <option value="" className="text-gray-900">No children linked</option>
                                        ) : (
                                            children.map(child => (
                                                <option key={child.student_id} value={child.student_id} className="text-gray-900">
                                                    {child.name} - Grade {child.grade || 'N/A'}
                                                </option>
                                            ))
                                        )}
                                    </select>
                                    <ChevronDown
                                        className="w-4 h-4 text-white absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"/>
                                </div>
                                <div className="digital-clock">
                                    <div className="text-lg font-bold">{formatTime(currentTime)}</div>
                                    <div className="text-xs">{formatDate(currentTime)}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </header>

                <div className="max-w-7xl mx-auto px-6 py-8">
                    {!currentChild ? (
                        <div className="text-center py-12">
                            <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                            <h2 className="text-2xl font-bold text-gray-600 mb-2">No Child Selected</h2>
                            <p className="text-gray-500 mb-4">
                                {children.length === 0 
                                    ? "No children are linked to your account. Please contact your school administrator."
                                    : "Please select a child from the dropdown above to view their dashboard."
                                }
                            </p>
                        </div>
                    ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                        {/* Left Column */}
                        <div className="space-y-8">

                            {/* Learner's Attendance */}
                            <div className="bg-white rounded-3xl shadow-xl p-6 card-parent">
                                <div className="flex items-center mb-6">
                                    <div className="bg-green-500 p-4 rounded-2xl mr-4 neon-glow">
                                        <Calendar className="w-8 h-8 text-white"/>
                                    </div>
                                    <div>
                                        <h2 className="text-2xl font-bold text-green-600">
                                            {currentChild?.name || 'Child'}'s Attendance
                                        </h2>
                                        <p className="text-gray-600 font-medium">
                                            {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} Overview
                                        </p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-7 gap-2 mb-6">
                                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, idx) => (
                                        <div key={idx}
                                             className="text-center text-xs font-bold text-gray-500 py-2">{day}</div>
                                    ))}

                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21].map((day) => {
                                        let className = 'calendar-day text-center py-3 text-sm ';
                                        if (day === 1 || day === 7 || day === 8 || day === 14 || day === 15 || day === 21) {
                                            className += 'text-gray-400';
                                        } else if (day === 9) {
                                            className += 'absent-day cursor-pointer font-bold';
                                        } else if (day === 20) {
                                            className += 'holiday-day cursor-pointer font-bold';
                                        } else {
                                            className += 'present-day cursor-pointer font-bold';
                                        }
                                        return (
                                            <div
                                                key={day}
                                                className={className}
                                                onClick={() => day !== 1 && day !== 7 && day !== 8 && day !== 14 && day !== 15 && day !== 21 && viewAttendanceDetails(`Dec ${day}`)}
                                            >
                                                {day}
                                            </div>
                                        );
                                    })}
                                </div>

                                <div className="bg-green-50 p-4 rounded-2xl mb-4">
                                    <div className="flex items-center justify-between">
                                        <div className="text-center">
                                            <div className="text-2xl font-bold text-green-600">
                                                {attendanceStats?.attendanceRate || 0}%
                                            </div>
                                            <div className="text-sm text-gray-600">Attendance Rate</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-2xl font-bold text-blue-600">
                                                {attendanceStats?.presentDays || 0}
                                            </div>
                                            <div className="text-sm text-gray-600">Present Days</div>
                                        </div>
                                        <div className="text-center">
                                            <div className="text-2xl font-bold text-red-600">
                                                {attendanceStats?.absentDays || 0}
                                            </div>
                                            <div className="text-sm text-gray-600">Missed Days</div>
                                        </div>
                                    </div>
                                </div>

                                <button
                                    onClick={() => alert('Opening complete attendance analytics... 📊 View detailed patterns, trends, and recommendations for Priya\'s attendance!')}
                                    className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-2xl transition-all duration-300 transform hover:scale-105"
                                >
                                    View Complete Attendance Report
                                </button>

                                {selectedDate && (
                                    <div className="mt-4 p-4 bg-green-50 rounded-2xl border-2 border-green-200">
                                        <h4 className="font-bold text-green-900 mb-2">Details for {selectedDate}</h4>
                                        <p className="text-sm text-green-800">Status: {getAttendanceData(selectedDate).status}</p>
                                        <p className="text-sm text-green-800">Check-in: {getAttendanceData(selectedDate).checkin}</p>
                                        <p className="text-sm text-green-800">Classes
                                            Attended: {getAttendanceData(selectedDate).classes}</p>
                                        <p className="text-xs text-gray-600 mt-2 italic">{getAttendanceData(selectedDate).note}</p>
                                    </div>
                                )}
                            </div>

                            {/* Daily Learnings */}
                            <div className="bg-white rounded-3xl shadow-xl p-6 card-parent">
                                <div className="flex items-center mb-6">
                                    <div className="bg-blue-500 p-4 rounded-2xl mr-4 neon-glow">
                                        <BookOpen className="w-8 h-8 text-white"/>
                                    </div>
                                    <div>
                                        <h2 className="text-2xl font-bold text-blue-600">Recent Learning</h2>
                                        <p className="text-gray-600 font-medium">
                                            {currentChild?.name || 'Child'}'s recent activities
                                        </p>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    {learningActivities.length > 0 ? (
                                        learningActivities.map((activity, index) => {
                                            const colors = ['green', 'blue', 'purple', 'orange', 'teal'];
                                            const color = colors[index % colors.length];
                                            const Icon = index === 0 ? CheckCircle : index === 1 ? Clock : FileText;
                                            
                                            return (
                                                <div key={activity.id} className={`learning-card p-4 rounded-2xl border-l-4 border-${color}-500`}>
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center">
                                                            <Icon className={`w-6 h-6 text-${color}-500 mr-3`}/>
                                                            <div>
                                                                <h3 className="font-bold text-gray-900">{activity.subject}</h3>
                                                                <p className="text-sm text-gray-600">
                                                                    {activity.topic} - {activity.status}
                                                                </p>
                                                                <p className={`text-xs text-${color}-600 font-medium`}>
                                                                    {activity.time}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="text-right">
                                                            <div className={`text-lg font-bold text-${color}-600`}>
                                                                {activity.score}%
                                                            </div>
                                                            <div className="text-xs text-gray-500">Score</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div className="text-center py-8">
                                            <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                                            <p className="text-gray-500">No recent learning activities found</p>
                                        </div>
                                    )}
                                </div>

                                <button
                                    onClick={() => alert('Loading detailed learning report... 📚 See comprehensive progress across all subjects and activities!')}
                                    className="w-full mt-4 bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-6 rounded-2xl transition-all duration-300 transform hover:scale-105"
                                >
                                    View Detailed Learning Report
                                </button>
                            </div>

                        </div>

                        {/* Middle Column */}
                        <div className="space-y-8">

                            {/* Learner's Competencies */}
                            <div className="bg-white rounded-3xl shadow-xl p-6 card-parent">
                                <div className="flex items-center mb-6">
                                    <div className="bg-purple-500 p-4 rounded-2xl mr-4 neon-glow">
                                        <Star className="w-8 h-8 text-white"/>
                                    </div>
                                    <div>
                                        <h2 className="text-2xl font-bold text-purple-600">
                                            {currentChild?.name || 'Child'}'s Competencies
                                        </h2>
                                        <p className="text-gray-600 font-medium">Skill development overview</p>
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    <div className="flex justify-center">
                                        <div className="competency-ring w-40 h-40">
                                            <div className="competency-center w-24 h-24">
                                                <div className="text-2xl font-bold text-purple-600">
                                                    {competencyData?.overall || 0}%
                                                </div>
                                                <div className="text-xs text-gray-600">Overall</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 text-center">
                                        <div className="bg-green-50 p-3 rounded-2xl">
                                            <div className="text-lg font-bold text-green-600">
                                                {competencyData?.problemSolving || 0}%
                                            </div>
                                            <div className="text-xs text-gray-600">Problem Solving</div>
                                        </div>
                                        <div className="bg-blue-50 p-3 rounded-2xl">
                                            <div className="text-lg font-bold text-blue-600">
                                                {competencyData?.criticalThinking || 0}%
                                            </div>
                                            <div className="text-xs text-gray-600">Critical Thinking</div>
                                        </div>
                                        <div className="bg-purple-50 p-3 rounded-2xl">
                                            <div className="text-lg font-bold text-purple-600">
                                                {competencyData?.communication || 0}%
                                            </div>
                                            <div className="text-xs text-gray-600">Communication</div>
                                        </div>
                                        <div className="bg-yellow-50 p-3 rounded-2xl">
                                            <div className="text-lg font-bold text-yellow-600">
                                                {competencyData?.leadership || 0}%
                                            </div>
                                            <div className="text-xs text-gray-600">Leadership</div>
                                        </div>
                                    </div>

                                    <div className="bg-purple-50 p-4 rounded-2xl">
                                        <h4 className="font-bold text-gray-900 mb-2">Growth Areas</h4>
                                        <ul className="text-sm text-gray-700 space-y-1">
                                            <li>• Strengthen mathematical reasoning</li>
                                            <li>• Enhance collaborative skills</li>
                                            <li>• Develop creative problem-solving</li>
                                        </ul>
                                    </div>
                                </div>

                                <button
                                    onClick={() => alert('Opening competency analysis dashboard... 🌟 Explore Priya\'s skill development and growth recommendations!')}
                                    className="w-full mt-4 bg-purple-500 hover:bg-purple-600 text-white font-bold py-3 px-6 rounded-2xl transition-all duration-300 transform hover:scale-105"
                                >
                                    View Detailed Competency Report
                                </button>
                            </div>

                            {/* Personalized Learning Results */}
                            <div className="bg-white rounded-3xl shadow-xl p-6 card-parent">
                                <div className="flex items-center mb-6">
                                    <div className="bg-green-500 p-4 rounded-2xl mr-4 neon-glow">
                                        <Award className="w-8 h-8 text-white"/>
                                    </div>
                                    <div>
                                        <h2 className="text-2xl font-bold text-green-600">Learning Results</h2>
                                        <p className="text-gray-600 font-medium">Personalized achievements</p>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div className="bg-green-50 p-4 rounded-2xl">
                                        <h3 className="font-bold text-gray-900 mb-3">Recent Assessments</h3>
                                        <div className="space-y-3">
                                            {assessmentResults.length > 0 ? (
                                                assessmentResults.map((assessment, index) => {
                                                    const colors = ['green', 'blue', 'purple'];
                                                    const color = colors[index % colors.length];
                                                    const percentage = Math.round((assessment.score / assessment.maxScore) * 100);
                                                    
                                                    return (
                                                        <div key={index}>
                                                            <div className="flex justify-between items-center">
                                                                <span className="text-sm font-medium">{assessment.name}</span>
                                                                <span className={`font-bold text-${color}-600`}>
                                                                    {assessment.score}/{assessment.maxScore}
                                                                </span>
                                                            </div>
                                                            <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                                                                <div className="progress-bar" style={{width: `${percentage}%`}}></div>
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                <div className="text-center py-4">
                                                    <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                                                    <p className="text-sm text-gray-500">No recent assessments</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="bg-blue-50 p-4 rounded-2xl">
                                        <h3 className="font-bold text-gray-900 mb-3">Learning Strengths</h3>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="text-center">
                                                <div className="text-2xl font-bold text-green-600">A+</div>
                                                <div className="text-xs text-gray-600">English</div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-2xl font-bold text-blue-600">A</div>
                                                <div className="text-xs text-gray-600">Mathematics</div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-2xl font-bold text-purple-600">A-</div>
                                                <div className="text-xs text-gray-600">Science</div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-2xl font-bold text-yellow-600">B+</div>
                                                <div className="text-xs text-gray-600">History</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <button
                                    onClick={() => alert('Generating personalized learning results... 📈 View detailed assessments, achievements, and improvement areas!')}
                                    className="w-full mt-4 bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-2xl transition-all duration-300 transform hover:scale-105"
                                >
                                    View Complete Results
                                </button>
                            </div>

                        </div>

                        {/* Right Column */}
                        <div className="space-y-8">

                            {/* Complete Holistic Progress */}
                            <div className="bg-white rounded-3xl shadow-xl p-6 card-parent">
                                <div className="flex items-center mb-6">
                                    <div className="bg-blue-500 p-4 rounded-2xl mr-4 neon-glow">
                                        <Shield className="w-8 h-8 text-white"/>
                                    </div>
                                    <div>
                                        <h2 className="text-2xl font-bold text-blue-600">Holistic Progress</h2>
                                        <p className="text-gray-600 font-medium">Complete development view</p>
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    <div className="bg-blue-50 p-4 rounded-2xl">
                                        <h3 className="font-bold text-gray-900 mb-3">Learning Domains</h3>
                                        <div className="space-y-3">
                                            <div>
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm font-medium">Cognitive Development</span>
                                                    <span className="font-bold text-blue-600">
                                                        {holisticProgress?.cognitive || 0}%
                                                    </span>
                                                </div>
                                                <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                                                    <div className="progress-bar" style={{width: `${holisticProgress?.cognitive || 0}%`}}></div>
                                                </div>
                                            </div>

                                            <div>
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm font-medium">Social-Emotional</span>
                                                    <span className="font-bold text-green-600">
                                                        {holisticProgress?.socialEmotional || 0}%
                                                    </span>
                                                </div>
                                                <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                                                    <div className="progress-bar" style={{width: `${holisticProgress?.socialEmotional || 0}%`}}></div>
                                                </div>
                                            </div>

                                            <div>
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm font-medium">Physical Development</span>
                                                    <span className="font-bold text-purple-600">
                                                        {holisticProgress?.physical || 0}%
                                                    </span>
                                                </div>
                                                <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                                                    <div className="progress-bar" style={{width: `${holisticProgress?.physical || 0}%`}}></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-green-50 p-4 rounded-2xl">
                                        <h3 className="font-bold text-gray-900 mb-3">Monthly Progress</h3>
                                        <div className="grid grid-cols-3 gap-4 text-center">
                                            <div>
                                                <div className="text-lg font-bold text-green-600">
                                                    +{holisticProgress?.monthlyProgress || 0}%
                                                </div>
                                                <div className="text-xs text-gray-600">This Month</div>
                                            </div>
                                            <div>
                                                <div className="text-lg font-bold text-blue-600">
                                                    {holisticProgress?.goalsMet || 0}
                                                </div>
                                                <div className="text-xs text-gray-600">Goals Met</div>
                                            </div>
                                            <div>
                                                <div className="text-lg font-bold text-purple-600">
                                                    {holisticProgress?.awards || 0}
                                                </div>
                                                <div className="text-xs text-gray-600">Awards</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-yellow-50 p-4 rounded-2xl">
                                        <h3 className="font-bold text-gray-900 mb-2">Teacher Feedback</h3>
                                        <p className="text-sm text-gray-700 italic">"Priya shows excellent analytical
                                            skills and consistently demonstrates leadership in group activities. Her
                                            curiosity and dedication to learning are commendable."</p>
                                        <p className="text-xs text-gray-500 mt-2">- Ms. Johnson, Class Teacher</p>
                                    </div>
                                </div>

                                <button
                                    onClick={() => alert('Loading complete holistic progress report... 🎯 Comprehensive view of Priya\'s overall development journey!')}
                                    className="w-full mt-4 bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-6 rounded-2xl transition-all duration-300 transform hover:scale-105"
                                >
                                    View Complete Progress Report
                                </button>
                            </div>

                            {/* Parent's Partnership Tracker */}
                            <div className="bg-white rounded-3xl shadow-xl p-6 card-parent" style={{
                                background: 'linear-gradient(135deg, #f3e8ff, #e0e7ff)',
                                border: '2px solid #7c3aed'
                            }}>
                                <div className="flex items-center mb-6">
                                    <div className="bg-purple-500 p-4 rounded-2xl mr-4 neon-glow">
                                        <Heart className="w-8 h-8 text-white"/>
                                    </div>
                                    <div>
                                        <h2 className="text-2xl font-bold text-purple-600">Partnership Tracker</h2>
                                        <p className="text-gray-600 font-medium">Your involvement & support</p>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div className="bg-purple-50 p-4 rounded-2xl">
                                        <h3 className="font-bold text-gray-900 mb-3">This Month's Activities</h3>
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm">Parent-Teacher Meetings</span>
                                                <span className="text-xs bg-green-200 text-green-800 px-2 py-1 rounded-full">
                                                    {partnershipData?.meetingsAttended || 0}/{partnershipData?.totalMeetings || 0} Attended
                                                </span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm">Homework Support Sessions</span>
                                                <span className="text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded-full">
                                                    {partnershipData?.homeworkSupportDays || 0}/{partnershipData?.totalDays || 0} Days
                                                </span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm">School Events Participation</span>
                                                <span className="text-xs bg-purple-200 text-purple-800 px-2 py-1 rounded-full">
                                                    {partnershipData?.eventsParticipated || 0}/{partnershipData?.totalEvents || 0} Events
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-blue-50 p-4 rounded-2xl">
                                        <h3 className="font-bold text-gray-900 mb-3">Upcoming Opportunities</h3>
                                        <div className="space-y-2">
                                            <div className="flex items-center">
                                                <Calendar className="w-4 h-4 text-blue-500 mr-2"/>
                                                <span className="text-sm">Science Fair - Dec 25</span>
                                            </div>
                                            <div className="flex items-center">
                                                <Users className="w-4 h-4 text-green-500 mr-2"/>
                                                <span className="text-sm">Parent Workshop - Jan 5</span>
                                            </div>
                                            <div className="flex items-center">
                                                <TrendingUp className="w-4 h-4 text-purple-500 mr-2"/>
                                                <span className="text-sm">Career Guidance Session - Jan 12</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-green-50 p-4 rounded-2xl">
                                        <h3 className="font-bold text-gray-900 mb-2">Partnership Score</h3>
                                        <div className="flex items-center justify-between">
                                            <span className="text-2xl font-bold text-green-600">
                                                {(partnershipData?.partnershipScore || 0) >= 90 ? 'Excellent' : 
                                                 (partnershipData?.partnershipScore || 0) >= 75 ? 'Good' : 
                                                 (partnershipData?.partnershipScore || 0) >= 60 ? 'Fair' : 'Needs Improvement'}
                                            </span>
                                            <span className="text-lg font-bold text-green-600">
                                                {partnershipData?.partnershipScore || 0}%
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-3 mt-2">
                                            <div className="progress-bar" style={{width: `${partnershipData?.partnershipScore || 0}%`}}></div>
                                        </div>
                                    </div>
                                </div>

                                <button
                                    onClick={() => alert('Opening partnership dashboard... 🤝 Track your involvement and discover new ways to support Priya\'s learning!')}
                                    className="w-full mt-4 bg-purple-500 hover:bg-purple-600 text-white font-bold py-3 px-6 rounded-2xl transition-all duration-300 transform hover:scale-105"
                                >
                                    View Partnership Dashboard
                                </button>
                            </div>
                        </div>
                    </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default HomePage;